'use client'
import dynamic from 'next/dynamic'
import { IconButton } from '@components/ui/button/IconButton'
import { MenuBulkIcon } from '@components/icons/bulk/MenuBulkIcon'

const NavMenu = dynamic(
  () => import('./nav-menu/NavMenu').then((m) => ({ default: m.NavMenu })),
  {
    ssr: false,
    loading: () => (
      <IconButton
        variant="primary"
        aria-label="Open navigation menu"
        className="hamburger-menu-button bg-cw-electric-purple text-white outline-1 outline-cw-electric-purple transition-all hover:bg-cw-electric-purple/80 hover:outline"
      >
        <MenuBulkIcon />
      </IconButton>
    ),
  },
)
import { Nav } from './nav'
import { FC, HTMLAttributes, useEffect, useRef, useState } from 'react'
import { cn } from '@utils'
import { Logo } from '@components/ui/Logo.tsx'
import { useIsAuthenticated } from '@domain/auth/Auth.context'
import { useWeb3Ready } from '@domain/auth/context/Web3ReadyContext'
import Toaster from '@components/shared/toaster/Toaster.tsx'
import { useCurrentBreakpoint } from '@hooks/useCurrentBreakpoint.hook'
import { ChatbotOverlay } from '@components/shared/ChatbotOverlay'
import { ChatbotFloatingButton } from '@components/shared/ChatbotFloatingButton'
import { PrivyAuthButton } from '@domain/auth/components/PrivyAuthButton'
import { MobileFixedNav } from './nav/MobileFixedNav'
// Import the buttons/contexts by file, not via the `cart`/`favorites` barrels: the
// barrels re-export the drawers, which would pull Privy/wagmi/qrcode into every route.
import { MarketplaceCartButton } from '@domain/marketplace/cart/MarketplaceCartButton'
import { useMarketplaceCart } from '@domain/marketplace/cart/MarketplaceCartContext'
import { MarketplaceFavoritesButton } from '@domain/marketplace/favorites/MarketplaceFavoritesButton'
import { useMarketplaceFavorites } from '@domain/marketplace/favorites/MarketplaceFavoritesContext'
import { MARKETPLACE_CART_PANEL_WIDTH_PX } from '@domain/marketplace/cart/marketplaceCartPanelWidth'
import { MarketplaceSearchButton } from '@domain/marketplace/components/search/MarketplaceSearchButton'

const MarketplaceCartDrawer = dynamic(
  () =>
    import('@domain/marketplace/cart/MarketplaceCartDrawer').then((m) => ({
      default: m.MarketplaceCartDrawer,
    })),
  { ssr: false },
)

const MarketplaceFavoritesDrawer = dynamic(
  () =>
    import('@domain/marketplace/favorites/MarketplaceFavoritesDrawer').then((m) => ({
      default: m.MarketplaceFavoritesDrawer,
    })),
  { ssr: false },
)

const MarketplaceSearchModal = dynamic(
  () =>
    import('@domain/marketplace/components/search/MarketplaceSearchModal').then((m) => ({
      default: m.MarketplaceSearchModal,
    })),
  { ssr: false },
)

const Notifications = dynamic(
  () =>
    import('@layouts/shared/header/notifications').then((m) => ({
      default: m.Notifications,
    })),
  { ssr: false },
)

// Lazy-load Account (heavy Web3 deps: privy, wagmi, viem) - only needed for authenticated users
const Account = dynamic(
  () =>
    import('@layouts/shared/header/account/Account').then((m) => ({
      default: m.Account,
    })),
  { ssr: false },
)

export type HeaderProps = HTMLAttributes<HTMLDivElement> & {
  theme?: 'light' | 'purple'
}

export const Header: FC<HeaderProps> = ({ className, theme = 'light' }) => {
  const [isChatbotOpen, setIsChatbotOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isAccountOpen, setIsAccountOpen] = useState(false)
  const [headerOpacity, setHeaderOpacity] = useState(0)
  const [isNavVisible, setIsNavVisible] = useState(true)
  const lastScrollYRef = useRef(0)
  const [isMounted, setIsMounted] = useState(false)
  const isAuthenticated = useIsAuthenticated()
  const web3Ready = useWeb3Ready()
  const showAccountWallet = isAuthenticated && web3Ready?.ready
  const { isMobile } = useCurrentBreakpoint()
  const { isOpen: isCartOpen, openCart, closeCart } = useMarketplaceCart()
  const {
    isOpen: isFavoritesOpen,
    openFavorites,
    closeFavorites,
  } = useMarketplaceFavorites()

  // Authenticated users need Web3 immediately (Account panel uses Wagmi hooks)
  useEffect(() => {
    if (isAuthenticated) {
      web3Ready?.activate()
    }
  }, [isAuthenticated])
  // Pages with dark hero backgrounds that need light header text
  const isPurpleTheme = theme === 'purple'

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    // Coalesce scroll events to one update per frame, and quantise the fade so the
    // 0-200px fade instead of once per scroll event. `lastScrollY` is a ref so the
    // listener is attached once rather than re-subscribed after every 10px.
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const scrollY = window.scrollY
        const maxScroll = 200
        const newOpacity = Math.min(Math.round((scrollY / maxScroll) * 20) / 20, 1)
        setHeaderOpacity(newOpacity)

        if (isMenuOpen || isAccountOpen) return

        if (isMobile) {
          // Mobile navigation hide/show logic
          const scrollDifference = scrollY - lastScrollYRef.current

          // Only trigger if scrolled more than 10px to avoid jittery behavior
          if (Math.abs(scrollDifference) > 10) {
            // Scrolling down past 100px hides navigation, scrolling up shows it
            setIsNavVisible(!(scrollDifference > 0 && scrollY > 100))
            lastScrollYRef.current = scrollY
          }
        } else {
          // Always show on desktop
          setIsNavVisible(true)
        }
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [isMobile, isMenuOpen, isAccountOpen])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const isDesktop = window.innerWidth >= 640
    const root = document.documentElement
    const body = document.body
    const hasOpenRightDrawer = isCartOpen || isFavoritesOpen
    const widthPx = `${MARKETPLACE_CART_PANEL_WIDTH_PX}px`

    if (!isDesktop) {
      root.style.removeProperty('--marketplace-cart-panel-width')
      body.style.paddingRight = ''
      body.style.transition = ''
      return
    }

    root.style.setProperty(
      '--marketplace-cart-panel-width',
      hasOpenRightDrawer ? widthPx : '0px',
    )
    body.style.transition = 'padding-right 300ms ease'
    body.style.paddingRight = hasOpenRightDrawer ? widthPx : '0px'

    return () => {
      root.style.removeProperty('--marketplace-cart-panel-width')
      body.style.paddingRight = ''
      body.style.transition = ''
    }
  }, [isCartOpen, isFavoritesOpen])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        !isSearchOpen &&
        document.activeElement?.tagName !== 'INPUT' &&
        document.activeElement?.tagName !== 'TEXTAREA'
      ) {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSearchOpen])

  const handleChatbotClick = () => {
    setIsChatbotOpen(true)
  }

  const handleChatbotClose = () => {
    setIsChatbotOpen(false)
  }

  return (
    <header>
      <nav
        className={cn(
          'h-16 sm:h-22',
          `header will-change fixed left-0 right-0 top-0 z-20 w-full flex items-center justify-center transition-opacity duration-300 ease-in-out`,
          {
            'opacity-100': isNavVisible,
            'opacity-0 pointer-events-none': !isNavVisible,
            'backdrop-blur-none': isPurpleTheme && headerOpacity === 0,
          },
          className,
        )}
        style={{
          backgroundColor: `rgba(255, 255, 255, ${headerOpacity})`,
        }}
      >
        <div className="max-w-container-lg h-full w-full justify-between relative flex items-center">
          <div className="relative md:absolute pl-4 pr-1 left-0 md:h-full flex items-center z-10">
            <Logo
              landingPage={isPurpleTheme}
              headerOpacity={isPurpleTheme ? headerOpacity : undefined}
            />
          </div>

          <Nav
            className="hidden sm:flex justify-center h-full"
            landingPage={isPurpleTheme}
            headerOpacity={isPurpleTheme ? headerOpacity : undefined}
          />

          <div className="md:absolute relative flex right-0 pr-4 items-center gap-4 z-10">
            <div className="hidden sm:flex items-center gap-4">
              {isMounted && (
                <MarketplaceSearchButton
                  onClick={() => {
                    setIsSearchOpen(true)
                  }}
                  landingPage={isPurpleTheme}
                  headerOpacity={isPurpleTheme ? headerOpacity : undefined}
                />
              )}
              {isMounted && (
                <MarketplaceFavoritesButton
                  onClick={() => {
                    closeCart()
                    openFavorites()
                  }}
                  landingPage={isPurpleTheme}
                  headerOpacity={isPurpleTheme ? headerOpacity : undefined}
                />
              )}
              {isMounted && (
                <MarketplaceCartButton
                  onClick={() => {
                    closeFavorites()
                    openCart()
                  }}
                  landingPage={isPurpleTheme}
                  headerOpacity={isPurpleTheme ? headerOpacity : undefined}
                />
              )}
            </div>
            {isMounted && isAuthenticated && (
              <Notifications
                landingPage={isPurpleTheme}
                headerOpacity={isPurpleTheme ? headerOpacity : undefined}
              />
            )}
            {isMounted && showAccountWallet && (
              <Account
                isOpen={isAccountOpen}
                onOpenChange={() => setIsAccountOpen(!isAccountOpen)}
              />
            )}
            <div className="flex gap-4 items-center">
              <div className="sm:hidden">
                <PrivyAuthButton
                  isAuthenticated={isAuthenticated}
                  buttonType="mobile"
                  landingPage={isPurpleTheme}
                  headerOpacity={isPurpleTheme ? headerOpacity : undefined}
                />
              </div>
              <div className="hidden sm:block">
                <PrivyAuthButton
                  isAuthenticated={isAuthenticated}
                  buttonType="default"
                  landingPage={isPurpleTheme}
                  headerOpacity={isPurpleTheme ? headerOpacity : undefined}
                />
              </div>
            </div>
            <NavMenu
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={setIsMenuOpen}
              onChatbotClick={handleChatbotClick}
            />
          </div>
        </div>
      </nav>

      {!isMenuOpen && (
        <div className="sm:hidden">
          <MobileFixedNav
            isMounted={isMounted}
            onSearchClick={() => setIsSearchOpen(true)}
            onFavoritesClick={() => {
              closeCart()
              openFavorites()
            }}
            onCartClick={() => {
              closeFavorites()
              openCart()
            }}
          />
        </div>
      )}

      <Toaster />

      <ChatbotFloatingButton onClick={handleChatbotClick} />

      <ChatbotOverlay isOpen={isChatbotOpen} onClose={handleChatbotClose} />
      {(isCartOpen || isFavoritesOpen) && (
        <div
          className="sm:hidden fixed inset-0 z-[21] bg-black/50"
          onClick={() => {
            closeCart()
            closeFavorites()
          }}
        />
      )}

      {isSearchOpen && (
        <MarketplaceSearchModal isOpen onClose={() => setIsSearchOpen(false)} />
      )}
      {isFavoritesOpen && (
        <MarketplaceFavoritesDrawer
          open
          onOpenChange={(open) => (open ? openFavorites() : closeFavorites())}
        />
      )}
      {isCartOpen && (
        <MarketplaceCartDrawer
          open
          onOpenChange={(open) => (open ? openCart() : closeCart())}
        />
      )}
    </header>
  )
}
